import { supabase } from './supabase';
import emailService from './emailService';
import cacheService from '../services/cacheService';
import { scanJobContent } from './contentFilter';

// Valid subscription tiers - must match the database enum
const VALID_SUBSCRIPTION_TIERS = ['free', 'pro', 'premium', 'agency'];
const PAID_TIERS = VALID_SUBSCRIPTION_TIERS.filter(t => t !== 'free');

/**
 * Validate subscription tier against allowed values
 * @param {string} tier - The tier to validate
 * @returns {string} - The validated tier, defaults to 'free' if invalid
 */
const validateSubscriptionTier = (tier) => {
  if (VALID_SUBSCRIPTION_TIERS.includes(tier)) {
    return tier;
  }
  console.warn(`Invalid subscription tier: ${tier}. Defaulting to 'free'`);
  return 'free';
};

/**
 * Look up the employer's subscription tier from user_profiles.
 * @param {string} employerId
 * @returns {Promise<{ tier: string|null, isPaid: boolean }>}
 */
const getEmployerTierInfo = async (employerId) => {
  if (!employerId) return { tier: null, isPaid: false };
  const { data: profile } = await supabase
    .from('user_profiles')
    .select('subscription_tier')
    .eq('id', employerId)
    .single();
  const tier = profile?.subscription_tier || null;
  return { tier, isPaid: PAID_TIERS.includes(tier) };
};

/**
 * Run content scan and apply moderation outcome via a single Supabase UPDATE.
 * Returns the fields that were updated on the job row (caller should merge into `data`).
 *
 * @param {string} jobId
 * @param {Object} jobFields - Text fields to scan (title, description, etc.)
 * @param {{ tier: string|null, isPaid: boolean }} tierInfo
 * @param {string} employerId
 * @param {string} logPrefix - e.g. '[employerService.createJob]'
 * @returns {Promise<{ scanned: boolean, contentPassed: boolean, updates: Object|null }>}
 */
const scanAndModerateJob = async (jobId, jobFields, tierInfo, employerId, logPrefix) => {
  let scanResult;
  try {
    scanResult = scanJobContent(jobFields);
  } catch (err) {
    console.warn(`${logPrefix} Content scan error (non-blocking):`, err.message);
    return { scanned: false, contentPassed: true, updates: null };
  }

  console.log(`${logPrefix} Content scan:`, {
    tier: tierInfo.tier || 'free',
    passed: scanResult.passed,
    flags: scanResult.flags.total_flags,
  });

  if (scanResult.passed && !tierInfo.isPaid) {
    // Clean + free tier → stays pending (normal moderation queue)
    return { scanned: true, contentPassed: true, updates: null };
  }

  // Build the UPDATE payload
  let updatePayload;
  if (!scanResult.passed) {
    // Flagged content → evaluation_needed regardless of tier
    updatePayload = {
      moderation_status: 'evaluation_needed',
      status: 'draft',
      content_flags: scanResult.flags,
      moderation_comment: scanResult.reason,
    };
  } else {
    // Clean + paid tier → auto-approve
    updatePayload = {
      moderation_status: 'approved',
      status: 'published',
      moderation_comment: `Auto-approved: clean content, ${tierInfo.tier} tier`,
      published_by: employerId,
      published_at: new Date().toISOString(),
    };
  }

  const { error } = await supabase
    .from('jobs')
    .update(updatePayload)
    .eq('id', jobId);

  if (error) {
    console.error(`${logPrefix} Post-scan update failed for job ${jobId}:`, error);
    return { scanned: true, contentPassed: scanResult.passed, updates: null };
  }

  console.log(`${logPrefix} Job ${jobId} → ${updatePayload.moderation_status}`);
  return { scanned: true, contentPassed: scanResult.passed, updates: updatePayload };
};

/**
 * Sanitize user profile data to ensure subscription_tier is valid
 * @param {object} profile - The user profile object
 * @returns {object} - Profile with sanitized subscription_tier
 */
const sanitizeUserProfile = (profile) => {
  if (!profile) return profile;
  
  if (profile.subscription_tier && !VALID_SUBSCRIPTION_TIERS.includes(profile.subscription_tier)) {
    console.warn(`Sanitizing invalid subscription_tier: ${profile.subscription_tier}`);
    return {
      ...profile,
      subscription_tier: validateSubscriptionTier(profile.subscription_tier)
    };
  }
  
  return profile;
};

const employerService = {
  // Get job by ID (for employers only)
  getJobById: async (jobId, employerId) => {
    try {
      if (!jobId) {
        return { success: false, error: 'Job ID is required' };
      }
      
      if (!employerId) {
        return { success: false, error: 'Employer ID is required' };
      }
      
      // Use a simpler query approach that's less prone to errors
      const { data, error } = await supabase
        .from('jobs')
        .select('id, title, description, requirements, responsibilities, job_type, experience_level, location, is_remote, skills_required, status, employer_id, created_at, updated_at')
        .eq('id', jobId)
        .eq('employer_id', employerId)
        .neq('status', 'deleted')
        .single();
      
      if (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Supabase query error:', error);
        }
        return { success: false, error: error.message || 'Failed to retrieve job details' };
      }
      
      if (!data) {
        return { success: false, error: 'Job not found or you do not have permission to view it' };
      }
      
      // Normalize field names for consistency
      const normalizedData = {
        ...data,
        // Ensure skills_required is always available
        skills_required: data.skills_required || data.required_skills || []
      };
      
      return { success: true, data: normalizedData };
    } catch (error) {
      console.error('Error in getJobById:', error);
      return { success: false, error: error.message || 'An unexpected error occurred' };
    }
  },
  
  // Get today's overview statistics
  getTodayOverview: async (employerId) => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayISOString = today.toISOString();
      
      // 1. Get applications count with a JOIN query
      const { data: applications, error: applicationsError } = await supabase
        .from('job_applications')
        .select(`
          id,
          job:job_id(employer_id)
        `)
        .gte('applied_at', todayISOString);
      
      if (applicationsError) {
        console.error('Error fetching applications:', applicationsError);
        return { success: false, error: 'Failed to fetch application data' };
      }
      
      // Filter applications for this employer
      const applicationsCount = applications.filter(app => 
        app.job && app.job.employer_id === employerId
      ).length;
      
      // 2. Get interviews count with a JOIN query
      const { data: interviews, error: interviewsError } = await supabase
        .from('job_applications')
        .select(`
          id,
          job:job_id(employer_id)
        `)
        .eq('status', 'interviewed')
        .gte('interview_scheduled_at', todayISOString)
        .lt('interview_scheduled_at', new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString());
      
      if (interviewsError) {
        console.error('Error fetching interviews:', interviewsError);
        return { success: false, error: 'Failed to fetch interview data' };
      }
      
      // Filter interviews for this employer
      const interviewsCount = interviews.filter(interview => 
        interview.job && interview.job.employer_id === employerId
      ).length;
      
      // 3. Get conversation IDs where employer is a participant
      const { data: conversations, error: conversationsError } = await supabase
        .from('conversations')
        .select('id')
        .or(`participant_1.eq.${employerId},participant_2.eq.${employerId}`);
      
      if (conversationsError) {
        console.error('Error fetching conversations:', conversationsError);
        return { success: false, error: 'Failed to fetch conversation data' };
      }
      
      // 4. Get count of conversations with new messages today (not sent by employer)
      let messagesCount = 0;
      if (conversations && conversations.length > 0) {
        const conversationIds = conversations.map(c => c.id);
        
        // Get distinct conversations that have new messages today from others
        const { data: messages, error: messagesError } = await supabase
          .from('messages')
          .select('conversation_id')
          .in('conversation_id', conversationIds)
          .neq('sender_id', employerId)
          .gte('created_at', todayISOString);
        
        if (messagesError) {
          console.error('Error fetching messages:', messagesError);
          return { success: false, error: 'Failed to fetch message data' };
        }
        
        // Count unique conversations, not individual messages
        const uniqueConversations = new Set(messages.map(m => m.conversation_id));
        messagesCount = uniqueConversations.size;
      }
      
      // 5. Get active jobs count
      const { data: jobs, error: jobsError } = await supabase
        .from('jobs')
        .select('id')
        .eq('employer_id', employerId)
        .eq('status', 'published');
      
      if (jobsError) {
        console.error('Error fetching jobs:', jobsError);
        return { success: false, error: 'Failed to fetch job data' };
      }
      
      const jobsCount = jobs.length;
      
      const overview = {
        newApplications: applicationsCount || 0,
        interviewsScheduled: interviewsCount || 0,
        newMessages: messagesCount || 0,
        activeJobs: jobsCount || 0
      };

      return { success: true, data: overview };
    } catch (error) {
      console.error('Error in getTodayOverview:', error);
      return { success: false, error: error.message || 'An unexpected error occurred' };
    }
  },

  // Get recent activities for an employer
  getRecentActivities: async (employerId, limit = 10) => {
    try {
      if (!employerId) {
        return { success: false, error: 'Employer ID is required' };
      }
      
      const activities = [];
      
      // Get activities from employer_activities table (includes interviews, etc.)
      const { data: employerActivities, error: activitiesError } = await supabase
        .from('employer_activities')
        .select('*')
        .eq('employer_id', employerId)
        .order('activity_date', { ascending: false })
        .limit(limit);

      if (activitiesError) {
        console.error('Error fetching employer activities:', activitiesError);
      } else {
        employerActivities.forEach(activity => {
          const activityTypeConfig = {
            'interview_scheduled': {
              icon: 'Calendar',
              color: 'text-success'
            },
            'interview_cancelled': {
              icon: 'X',
              color: 'text-error'
            },
            'interview_rescheduled': {
              icon: 'CalendarX',
              color: 'text-warning'
            },
            'interview_completed': {
              icon: 'CheckCircle',
              color: 'text-success'
            },
            'job_posted': {
              icon: 'Plus',
              color: 'text-primary'
            },
            'application_received': {
              icon: 'User',
              color: 'text-primary'
            },
            'candidate_viewed': {
              icon: 'Eye',
              color: 'text-info'
            },
            'message_sent': {
              icon: 'MessageSquare',
              color: 'text-accent'
            },
            'contract_started': {
              icon: 'CheckCircle',
              color: 'text-success'
            },
            'contract_ended': {
              icon: 'X',
              color: 'text-error'
            }
          };

          const config = activityTypeConfig[activity.activity_type] || {
            icon: 'Activity',
            color: 'text-text-secondary'
          };

          activities.push({
            id: activity.id,
            type: activity.activity_type,
            message: activity.description,
            time: activity.activity_date,
            icon: config.icon,
            color: config.color,
            data: activity.metadata || {}
          });
        });
      }
      
      // Get recent applications (fallback for non-tracked activities)
      const { data: recentApplications, error: applicationsError } = await supabase
        .from('job_applications')
        .select(`
          id,
          status,
          applied_at,
          job:jobs!inner(
            id,
            title,
            employer_id
          ),
          applicant:user_profiles!job_applications_applicant_id_fkey(
            id,
            full_name,
            profile_picture
          )
        `)
        .eq('job.employer_id', employerId)
        .order('applied_at', { ascending: false })
        .limit(5);

      if (applicationsError) {
        console.error('Error fetching applications:', applicationsError);
      } else {
        recentApplications.forEach(app => {
          // Only add if not already in employer_activities
          const exists = activities.some(a => a.data?.applicationId === app.id);
          if (!exists) {
            activities.push({
              id: `app-${app.id}`,
              type: 'application',
              message: `New application from ${app.applicant.full_name}`,
              time: app.applied_at,
              icon: 'User',
              color: 'text-primary',
              data: {
                applicationId: app.id,
                jobId: app.job.id,
                jobTitle: app.job.title,
                applicantId: app.applicant.id,
                applicantName: app.applicant.full_name,
                applicantAvatar: app.applicant.profile_picture
              }
            });
          }
        });
      }

      // Get recent messages - Using a simpler approach
      const { data: conversations, error: conversationsError } = await supabase
        .from('conversations')
        .select(`
          id,
          participant_1,
          participant_2,
          last_message_at
        `)
        .or(`participant_1.eq.${employerId},participant_2.eq.${employerId}`)
        .order('last_message_at', { ascending: false })
        .limit(5);

      if (conversationsError) {
        console.error('Error fetching conversations:', conversationsError);
      } else {
        // Process each conversation sequentially
        for (const conv of conversations) {
          // Determine the other participant ID
          const otherParticipantId = conv.participant_1 === employerId 
            ? conv.participant_2 
            : conv.participant_1;
          
          // Get the other participant's profile
          const { data: profileData, error: profileError } = await supabase
            .from('user_profiles')
            .select('id, full_name, profile_picture')
            .eq('id', otherParticipantId)
            .single();
            
          if (profileError) {
            console.error('Error fetching profile:', profileError);
            continue;
          }
          
          // Get the last message
          const { data: lastMessage, error: messageError } = await supabase
            .from('messages')
            .select('id, content, sender_id, created_at')
            .eq('conversation_id', conv.id)
            .order('created_at', { ascending: false })
            .limit(1);
            
          // Check if we have any messages
          const hasMessages = !messageError && lastMessage && lastMessage.length > 0;
          
          if (profileData) {
            activities.push({
              id: `msg-${conv.id}`,
              type: 'message',
              message: `Message from ${profileData.full_name}`,
              time: hasMessages ? lastMessage[0].created_at : conv.last_message_at,
              icon: 'MessageSquare',
              color: 'text-accent',
              data: {
                conversationId: conv.id,
                senderId: profileData.id,
                senderName: profileData.full_name,
                senderAvatar: profileData.profile_picture,
                preview: hasMessages ? lastMessage[0].content : 'No messages yet'
              }
            });
          }
        }
      }

      // Get upcoming interviews
      const { data: interviews, error: interviewsError } = await supabase
        .from('job_applications')
        .select(`
          id,
          interview_scheduled_at,
          job:jobs!inner(
            id,
            title,
            employer_id
          ),
          applicant:user_profiles!job_applications_applicant_id_fkey(
            id,
            full_name,
            profile_picture
          )
        `)
        .eq('job.employer_id', employerId)
        .eq('status', 'interviewed')
        .gte('interview_scheduled_at', new Date().toISOString())
        .order('interview_scheduled_at', { ascending: true })
        .limit(3);

      if (interviewsError) {
        console.error('Error fetching interviews:', interviewsError);
      } else {
        interviews.forEach(interview => {
          const interviewDate = new Date(interview.interview_scheduled_at);
          const now = new Date();
          const hoursDifference = Math.round((interviewDate - now) / (1000 * 60 * 60));
          
          let timeMessage;
          if (hoursDifference < 1) {
            timeMessage = 'in less than an hour';
          } else if (hoursDifference === 1) {
            timeMessage = 'in 1 hour';
          } else if (hoursDifference < 24) {
            timeMessage = `in ${hoursDifference} hours`;
          } else {
            const daysDifference = Math.floor(hoursDifference / 24);
            timeMessage = `in ${daysDifference} ${daysDifference === 1 ? 'day' : 'days'}`;
          }
          
          activities.push({
            id: `int-${interview.id}`,
            type: 'interview',
            message: `Interview with ${interview.applicant.full_name} ${timeMessage}`,
            time: interview.interview_scheduled_at,
            icon: 'Clock',
            color: 'text-warning',
            data: {
              applicationId: interview.id,
              jobId: interview.job.id,
              jobTitle: interview.job.title,
              applicantId: interview.applicant.id,
              applicantName: interview.applicant.full_name,
              applicantAvatar: interview.applicant.profile_picture,
              scheduledAt: interview.interview_scheduled_at
            }
          });
        });
      }

      // Get recent hires
      const { data: recentHires, error: hiresError } = await supabase
        .from('job_applications')
        .select(`
          id,
          updated_at,
          job:jobs!inner(
            id,
            title,
            employer_id
          ),
          applicant:user_profiles!job_applications_applicant_id_fkey(
            id,
            full_name,
            profile_picture
          )
        `)
        .eq('job.employer_id', employerId)
        .eq('status', 'accepted')
        .order('updated_at', { ascending: false })
        .limit(3);

      if (hiresError) {
        console.error('Error fetching hires:', hiresError);
      } else {
        recentHires.forEach(hire => {
          activities.push({
            id: `hire-${hire.id}`,
            type: 'hire',
            message: `Successfully hired ${hire.applicant.full_name}`,
            time: hire.updated_at,
            icon: 'CheckCircle',
            color: 'text-success',
            data: {
              applicationId: hire.id,
              jobId: hire.job.id,
              jobTitle: hire.job.title,
              applicantId: hire.applicant.id,
              applicantName: hire.applicant.full_name,
              applicantAvatar: hire.applicant.profile_picture
            }
          });
        });
      }

      // Sort all activities by time (newest first)
      activities.sort((a, b) => new Date(b.time) - new Date(a.time));

      // Format the timestamps to relative time
      activities.forEach(activity => {
        const activityTime = new Date(activity.time);
        const now = new Date();
        const diffMinutes = Math.floor((now - activityTime) / (1000 * 60));
        
        if (diffMinutes < 1) {
          activity.time = 'just now';
        } else if (diffMinutes < 60) {
          activity.time = `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
        } else {
          const diffHours = Math.floor(diffMinutes / 60);
          if (diffHours < 24) {
            activity.time = `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
          } else {
            const diffDays = Math.floor(diffHours / 24);
            activity.time = `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
          }
        }
      });

      return { success: true, data: activities.slice(0, limit) };
    } catch (error) {
      console.error('Error in getRecentActivities:', error);
      return { success: false, error: 'Failed to load recent activities' };
    }
  },
  
  // Get all activities for the activities page
  getAllActivities: async (employerId) => {
    try {
      const { data, error } = await supabase
        .from('employer_activities')
        .select('*')
        .eq('employer_id', employerId)
        .order('activity_date', { ascending: false });

      if (error) {
        console.error('Error fetching activities:', error);
        return { success: false, error: error.message };
      }
      
      return { success: true, data };
    } catch (error) {
      console.error('Error in getAllActivities:', error);
      return { success: false, error: 'Failed to load activities' };
    }
  },

  // Get all jobs for an employer
  getEmployerJobs: async (employerId) => {
    try {
      if (!employerId) {
        return { success: false, error: 'Employer ID is required' };
      }

      const { data, error } = await supabase
        .from('jobs')
        .select(`
          *,
          applications:job_applications(count)
        `)
        .eq('employer_id', employerId)
        .neq('status', 'deleted')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching employer jobs:', error);
        return { success: false, error: error.message };
      }

      // Format the applications count and transform snake_case to camelCase for frontend
      const formattedJobs = data.map(job => ({
        ...job,
        application_count: job.applications[0]?.count || 0,
        // Transform expires_at to expiresAt for frontend components
        expiresAt: job.expires_at,
        // Also transform application_deadline to applicationDeadline as a fallback
        applicationDeadline: job.application_deadline
      }));
      
      return { success: true, data: formattedJobs };
    } catch (error) {
      console.error('Error in getEmployerJobs:', error);
      return { success: false, error: 'Failed to load employer jobs' };
    }
  },

  // Get all job applications for an employer
  getJobApplications: async (employerId) => {
    try {
      if (!employerId) {
        return { success: false, error: 'Employer ID is required' };
      }

      const { data, error } = await supabase
        .from('job_applications')
        .select(`
          *,
          job:jobs!inner(id, title, employer_id),
          applicant:user_profiles!job_applications_applicant_id_fkey(*)
        `)
        .eq('job.employer_id', employerId)
        .order('applied_at', { ascending: false });

      if (error) {
        console.error('Error fetching job applications:', error);
        return { success: false, error: error.message };
      }
      
      return { success: true, data };
    } catch (error) {
      console.error('Error in getJobApplications:', error);
      return { success: false, error: 'Failed to load job applications' };
    }
  },

  // Get all hired VAs for an employer
  getHiredVAs: async (employerId) => {
    try {
      if (!employerId) {
        return { success: false, error: 'Employer ID is required' };
      }

      const { data, error } = await supabase
        .from('job_applications')
        .select(`
          id,
          job_id,
          applicant_id,
          status,
          updated_at,
          applied_at,
          job:jobs!inner(id, title, employer_id),
          va:user_profiles!job_applications_applicant_id_fkey(
            id, 
            full_name, 
            profile_picture, 
            hourly_rate, 
            skills, 
            location, 
            experience_years,
            bio
          )
        `)
        .eq('job.employer_id', employerId)
        .eq('status', 'accepted')
        .order('updated_at', { ascending: false });

      if (error) {
        console.error('Error fetching hired VAs:', error);
        return { success: false, error: error.message };
      }

      // Transform the data to match the expected format
      const hiredVAs = data.map(item => ({
        id: item.id,
        va_id: item.applicant_id,
        job_id: item.job_id,
        status: 'active', // Default to active for now
        name: item.va?.full_name || 'Unknown',
        avatar: item.va?.profile_picture || null,
        title: item.job?.title || 'Unknown Job',
        role: item.va?.bio?.split('.')[0] || 'Virtual Assistant',
        hourlyRate: item.va?.hourly_rate || 0,
        skills: item.va?.skills || [],
        location: item.va?.location || 'Unknown',
        experience: item.va?.experience_years || 0,
        bio: item.va?.bio || '',
        hiredDate: item.updated_at || item.applied_at,
        startDate: new Date(item.updated_at || item.applied_at).toLocaleDateString(),
        // Add mock performance data since it's required by the component
        performance: {
          rating: (4 + Math.random()).toFixed(1), // Random rating between 4.0 and 5.0
          tasksCompleted: Math.floor(Math.random() * 100), // Random number of tasks
          hoursWorked: Math.floor(Math.random() * 80), // Random hours worked
          totalPaid: (Math.random() * 2000).toFixed(2) // Random amount paid
        }
      }));
      
      return { success: true, data: hiredVAs };
    } catch (error) {
      console.error('Error in getHiredVAs:', error);
      return { success: false, error: 'Failed to load hired VAs' };
    }
  },

  // Get analytics data for an employer
  getEmployerAnalytics: async (employerId) => {
    try {
      if (!employerId) {
        return { success: false, error: 'Employer ID is required' };
      }

      // Get total jobs by status
      const { data: jobsData, error: jobsError } = await supabase
        .from('jobs')
        .select('id, status')
        .eq('employer_id', employerId)
        .neq('status', 'deleted');

      if (jobsError) {
        console.error('Error fetching jobs for analytics:', jobsError);
        return { success: false, error: jobsError.message };
      }

      // Count jobs by status
      const jobStatusCounts = {
        draft: 0,
        published: 0,
        paused: 0,
        filled: 0,
        expired: 0,
        archived: 0
      };

      jobsData.forEach(job => {
        if (job.status in jobStatusCounts) {
          jobStatusCounts[job.status]++;
        }
      });

      // Get applications data
      const { data: applicationsData, error: applicationsError } = await supabase
        .from('job_applications')
        .select(`
          id, 
          status,
          job:jobs!inner(employer_id)
        `)
        .eq('job.employer_id', employerId);

      if (applicationsError) {
        console.error('Error fetching applications for analytics:', applicationsError);
        return { success: false, error: applicationsError.message };
      }

      // Count applications by status
      const applicationStatusCounts = {
        pending: 0,
        shortlisted: 0,
        interviewed: 0,
        accepted: 0,
        rejected: 0
      };

      applicationsData.forEach(app => {
        if (app.status in applicationStatusCounts) {
          applicationStatusCounts[app.status]++;
        }
      });

      // Prepare analytics object
      const analytics = {
        jobs: {
          total: jobsData.length,
          byStatus: jobStatusCounts
        },
        applications: {
          total: applicationsData.length,
          byStatus: applicationStatusCounts
        },
        // Add other metrics as needed
      };

      return { success: true, data: analytics };
    } catch (error) {
      console.error('Error in getEmployerAnalytics:', error);
      return { success: false, error: 'Failed to load analytics data' };
    }
  },

  // Check if a job with the same title already exists for an employer
  checkDuplicateJob: async (title, employerId) => {
    try {
      if (!title || !employerId) {
        return { exists: false, job: null };
      }

      const { data, error } = await supabase
        .from('jobs')
        .select('id, title, description, job_type, experience_level, location, is_remote, skills_required, status, employer_id, created_at, updated_at')
        .eq('employer_id', employerId)
        .ilike('title', title.trim())
        .in('status', ['draft', 'published']);

      if (error) throw error;
      
      if (data && data.length > 0) {
        return { exists: true, job: data[0] };
      }
      
      return { exists: false, job: null };
    } catch (error) {
      console.error('Error checking duplicate job:', error);
      return { exists: false, job: null };
    }
  },

  // Create a new job posting
  createJob: async (jobData) => {
    try {
      // Determine employer tier for moderation logic
      const tierInfo = await getEmployerTierInfo(jobData.employer_id);

      // Free tier: always force draft + pending moderation on insert
      // Paid tier: insert as-is; post-insert scan will auto-approve or flag
      if (!tierInfo.isPaid && jobData.status === 'published') {
        jobData.status = 'draft';
        jobData.moderation_status = 'pending';
      }

      // First check if a job with the same title already exists
      const duplicateCheck = await employerService.checkDuplicateJob(jobData.title, jobData.employer_id);
      
      // If a duplicate exists, update that job instead of creating a new one
      if (duplicateCheck.exists) {
        // Use the existing job's ID to update it
        const result = await employerService.updateJob(duplicateCheck.job.id, jobData);
        // Add a flag to indicate this was a duplicate that was updated
        if (result.success) {
          result.wasDuplicate = true;
        }
        return result;
      }

      // Whitelist only valid job fields for insertion
      const validJobFields = [
        'employer_id', 'title', 'description', 'requirements', 'skills_required',
        'job_type', 'experience_level', 'budget_min', 'budget_max', 'currency',
        'location', 'is_remote', 'status', 'moderation_status', 'expires_at', 'application_deadline',
        'responsibilities', 'benefits', 'start_date', 'company_name', 'hourly_rate_min',
        'hourly_rate_max', 'min_experience_years', 'ml_requirements', 'difficulty_score',
        'complexity_rating', 'cultural_requirements', 'success_criteria', 'public_slug'
      ];

      // Build normalized data with only valid fields
      const normalizedJobData = {};
      validJobFields.forEach(field => {
        if (Object.prototype.hasOwnProperty.call(jobData, field)) {
          normalizedJobData[field] = jobData[field];
        }
      });

      // Handle skills field mapping: normalize both required_skills and skills_required
      // prepareJobPayload returns 'required_skills', but database column is 'skills_required'
      if (jobData.required_skills && !normalizedJobData.skills_required) {
        normalizedJobData.skills_required = jobData.required_skills;
      }
      if (!normalizedJobData.skills_required) {
        normalizedJobData.skills_required = [];
      }

      // Set drafted_by to the employer creating the job
      normalizedJobData.drafted_by = jobData.employer_id;
      normalizedJobData.drafted_at = new Date().toISOString();

      const { data, error } = await supabase
        .from('jobs')
        .insert([normalizedJobData])
        .select('id, title, description, requirements, job_type, experience_level, location, is_remote, skills_required, status, moderation_status, employer_id, created_at, updated_at')
        .single();

      if (error) {
        console.error('[employerService.createJob] Error creating job:', {
          code: error.code,
          message: error.message,
          details: error.details || null
        });
        return { success: false, error: error.message || 'Failed to create job' };
      }

      // Post-insert: scan content and apply moderation outcome
      if (data?.id) {
        const jobFields = {
          title: data.title,
          description: data.description,
          requirements: Array.isArray(data.requirements)
            ? data.requirements.join(' ')
            : data.requirements,
          qualifications: data.qualifications,
          skills_required: data.skills_required,
          category: data.category,
        };
        const modResult = await scanAndModerateJob(
          data.id, jobFields, tierInfo, jobData.employer_id, '[employerService.createJob]'
        );
        if (modResult.updates) {
          Object.assign(data, modResult.updates);
        }
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in createJob:', error);
      return { success: false, error: error.message || 'An unexpected error occurred' };
    }
  },

  // Update existing job
  updateJob: async (jobId, jobData) => {
    try {
      if (!jobId) {
        return { success: false, error: 'Job ID is required' };
      }

      if (!jobData.employer_id) {
        return { success: false, error: 'Employer ID is required' };
      }

      // Whitelist only valid job fields for updates
      const validJobFields = [
        'title', 'description', 'requirements', 'skills_required',
        'job_type', 'experience_level', 'budget_min', 'budget_max', 'currency',
        'location', 'is_remote', 'status', 'moderation_status', 'expires_at', 'application_deadline',
        'responsibilities', 'benefits', 'start_date', 'company_name', 'hourly_rate_min',
        'hourly_rate_max', 'min_experience_years', 'ml_requirements', 'difficulty_score',
        'complexity_rating', 'cultural_requirements', 'success_criteria', 'public_slug'
      ];

      // Build normalized data with only valid fields
      const normalizedJobData = { updated_at: new Date().toISOString() };
      validJobFields.forEach(field => {
        if (Object.prototype.hasOwnProperty.call(jobData, field)) {
          normalizedJobData[field] = jobData[field];
        }
      });

      // Handle skills field mapping: normalize both required_skills and skills_required
      // prepareJobPayload returns 'required_skills', but database column is 'skills_required'
      if (jobData.required_skills && !normalizedJobData.skills_required) {
        normalizedJobData.skills_required = jobData.required_skills;
      }
      if (!normalizedJobData.skills_required) {
        normalizedJobData.skills_required = [];
      }

      // If status is changing to 'published', set published_by and published_at
      if (normalizedJobData.status === 'published') {
        normalizedJobData.published_by = jobData.employer_id;
        normalizedJobData.published_at = new Date().toISOString();
      }

      const { data, error } = await supabase
        .from('jobs')
        .update(normalizedJobData)
        .eq('id', jobId)
        .eq('employer_id', jobData.employer_id)
        .select('id, title, description, requirements, job_type, experience_level, location, is_remote, skills_required, status, moderation_status, employer_id, created_at, updated_at')
        .single();

      if (error) {
        console.error('[employerService.updateJob] Error updating job:', {
          code: error.code,
          message: error.message,
          details: error.details || null
        });
        return { success: false, error: error.message || 'Failed to update job' };
      }

      if (!data) {
        return { success: false, error: 'Job not found or you do not have permission to update it' };
      }

      // Re-scan content on every update — flag regardless of tier, auto-approve paid clean jobs
      if (data.id) {
        const updateTierInfo = await getEmployerTierInfo(jobData.employer_id);
        const jobFields = {
          title: data.title || jobData.title,
          description: data.description || jobData.description,
          requirements: Array.isArray(data.requirements || jobData.requirements)
            ? (data.requirements || jobData.requirements || []).join(' ')
            : (data.requirements || jobData.requirements),
          qualifications: data.qualifications || jobData.qualifications,
          skills_required: data.skills_required || jobData.skills_required,
          category: data.category || jobData.category,
        };
        const modResult = await scanAndModerateJob(
          data.id, jobFields, updateTierInfo, jobData.employer_id, '[employerService.updateJob]'
        );
        if (modResult.updates) {
          Object.assign(data, modResult.updates);
        }
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in updateJob:', error);
      return { success: false, error: error.message || 'An unexpected error occurred' };
    }
  },

  // Delete a job posting
  // Uses soft-delete (status='deleted') when contracts/timesheet data exist to preserve financial records.
  // Falls back to hard-delete only when no dependent contracts exist.
  deleteJob: async (jobId, employerId) => {
    try {
      if (!jobId) {
        return { success: false, error: 'Job ID is required' };
      }

      if (!employerId) {
        return { success: false, error: 'Employer ID is required' };
      }

      // First check if the job exists and belongs to this employer
      const { error: jobError } = await supabase
        .from('jobs')
        .select('id')
        .eq('id', jobId)
        .eq('employer_id', employerId)
        .single();

      if (jobError) {
        console.error('Job verification error:', jobError);
        return { success: false, error: 'Job not found or you do not have permission to delete it' };
      }

      // Check if job has related contracts (which may have timesheet entries, reviews, etc.)
      const { count: contractCount, error: countError } = await supabase
        .from('contracts')
        .select('id', { count: 'exact', head: true })
        .eq('job_id', jobId);

      if (countError) {
        console.error('Error checking job contracts:', countError);
        return { success: false, error: 'Failed to verify job dependencies before deletion' };
      }

      if ((contractCount ?? 0) > 0) {        // Soft-delete: preserve job and all related financial/audit records
        const { error: updateError } = await supabase
          .from('jobs')
          .update({ status: 'deleted', updated_at: new Date().toISOString() })
          .eq('id', jobId)
          .eq('employer_id', employerId);

        if (updateError) {
          console.error('Soft-delete job error:', updateError);
          return { success: false, error: 'Failed to delete job' };
        }
      } else {
        // Hard-delete: no contracts, safe to remove entirely
        const { error } = await supabase
          .from('jobs')
          .delete()
          .eq('id', jobId)
          .eq('employer_id', employerId);

        if (error) {
          console.error('Delete job error:', error);
          return { success: false, error: 'Failed to delete job' };
        }
      }

      // Invalidate dashboard cache
      await cacheService.setDashboardStats(employerId, 'employer', null, 0);

      return { success: true, message: 'Job deleted successfully' };
    } catch (error) {
      console.error('Error in deleteJob:', error);
      return { success: false, error: error.message || 'An unexpected error occurred' };
    }
  },

  // Duplicate a job posting
  duplicateJob: async (jobId, employerId) => {
    try {
      if (!jobId) {
        return { success: false, error: 'Job ID is required' };
      }

      if (!employerId) {
        return { success: false, error: 'Employer ID is required' };
      }

      // First get the job to duplicate
      const { data: jobToDuplicate, error: jobError } = await supabase
        .from('jobs')
        .select('id, title, description, job_type, experience_level, location, is_remote, skills_required, status, employer_id, created_at, updated_at')
        .eq('id', jobId)
        .eq('employer_id', employerId)
        .neq('status', 'deleted')
        .single();

      if (jobError) {
        console.error('Job fetch error:', jobError);
        return { success: false, error: 'Job not found or you do not have permission to duplicate it' };
      }

      // Import slug generation function
      const { generateJobSlug } = await import('./slugGenerator.js');
      const newTitle = `${jobToDuplicate.title} (Copy)`;
      const newSlug = generateJobSlug(newTitle);

      // Create a new job based on the existing one
      const newJob = {
        ...jobToDuplicate,
        id: undefined, // Remove ID so a new one is generated
        title: newTitle,
        status: 'draft', // Always start as draft
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        employer_id: employerId,
        public_slug: newSlug // Set slug during creation
      };

      // Remove any fields that shouldn't be duplicated
      delete newJob.id;
      delete newJob.application_count;

      // Insert the new job
      const { data, error } = await supabase
        .from('jobs')
        .insert([newJob])
        .select()
        .single();

      if (error) {
        console.error('Duplicate job error:', error);
        return { success: false, error: error.message || 'Failed to duplicate job' };
      }

      // Invalidate dashboard cache
      await cacheService.setDashboardStats(employerId, 'employer', null, 0);

      return { success: true, data, message: 'Job duplicated successfully' };
    } catch (error) {
      console.error('Error in duplicateJob:', error);
      return { success: false, error: error.message || 'An unexpected error occurred' };
    }
  },

  // Update application status
  updateApplicationStatus: async (applicationId, status, notes = '') => {
    try {
      if (!applicationId) {
        return { success: false, error: 'Application ID is required' };
      }

      if (!status) {
        return { success: false, error: 'Status is required' };
      }

      const validStatuses = ['pending', 'shortlisted', 'interviewed', 'accepted', 'rejected'];
      if (!validStatuses.includes(status)) {
        return { success: false, error: 'Invalid status. Must be one of: ' + validStatuses.join(', ') };
      }

      const updates = {
        status,
        updated_at: new Date().toISOString()
      };

      if (notes) {
        updates.employer_notes = notes;
      }

      const { data, error } = await supabase
        .from('job_applications')
        .update(updates)
        .eq('id', applicationId)
        .select(`
          *,
          job:jobs(id, title, employer_id),
          applicant:user_profiles!job_applications_applicant_id_fkey(id, full_name, email)
        `)
        .single();

      if (error) {
        console.error('Update application status error:', error);
        return { success: false, error: error.message || 'Failed to update application status' };
      }

      if (!data) {
        return { success: false, error: 'Application not found' };
      }

      // Invalidate dashboard cache for the employer
      if (data.job?.employer_id) {
        await cacheService.setDashboardStats(data.job.employer_id, 'employer', null, 0);
      }

      return { success: true, data, message: `Application status updated to ${status}` };
    } catch (error) {
      console.error('Error in updateApplicationStatus:', error);
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { success: false, error: error.message || 'An unexpected error occurred' };
    }
  },

  // Update job status (published/draft)
  updateJobStatus: async (jobId, status, employerId) => {
    try {
      if (!jobId) {
        return { success: false, error: 'Job ID is required' };
      }

      if (!employerId) {
        return { success: false, error: 'Employer ID is required' };
      }

      if (!status || !['draft', 'published', 'archived', 'closed'].includes(status)) {
        return { success: false, error: 'Valid status is required (draft, published, archived, or closed)' };
      }

      // Update the job status
      const { data, error } = await supabase
        .from('jobs')
        .update({
          status,
          updated_at: new Date().toISOString()
        })
        .eq('id', jobId)
        .eq('employer_id', employerId)
        .select()
        .single();

      if (error) {
        console.error('Update job status error:', error);
        return { success: false, error: error.message || 'Failed to update job status' };
      }

      if (!data) {
        return { success: false, error: 'Job not found or you do not have permission to update it' };
      }

      // Invalidate dashboard cache
      await cacheService.setDashboardStats(employerId, 'employer', null, 0);

      return { success: true, data, message: `Job status updated to ${status}` };
    } catch (error) {
      console.error('Error in updateJobStatus:', error);
      return { success: false, error: error.message || 'An unexpected error occurred' };
    }
  },

  // Refresh job posting - updates the updated_at timestamp to boost visibility
  refreshJobPosting: async (jobId, employerId) => {
    try {
      if (!jobId) {
        return { success: false, error: 'Job ID is required' };
      }

      if (!employerId) {
        return { success: false, error: 'Employer ID is required' };
      }

      // Update the job's updated_at timestamp to refresh it
      const { data, error } = await supabase
        .from('jobs')
        .update({
          updated_at: new Date().toISOString()
        })
        .eq('id', jobId)
        .eq('employer_id', employerId)
        .select()
        .single();

      if (error) {
        console.error('Refresh job posting error:', error);
        return { success: false, error: error.message || 'Failed to refresh job posting' };
      }

      if (!data) {
        return { success: false, error: 'Job not found or you do not have permission to refresh it' };
      }

      return { success: true, data, message: 'Job posting refreshed successfully' };
    } catch (error) {
      console.error('Error in refreshJobPosting:', error);
      return { success: false, error: error.message || 'An unexpected error occurred' };
    }
  },
  // Schedule interview for a job application
  scheduleInterview: async (applicationId, interviewData) => {
    try {
      if (!applicationId) {
        return { success: false, error: 'Application ID is required' };
      }

      if (!interviewData || !interviewData.scheduledAt) {
        return { success: false, error: 'Interview scheduled date/time is required' };
      }

      // Update the job application with interview details
      const { data, error } = await supabase
        .from('job_applications')
        .update({
          interview_scheduled_at: interviewData.scheduledAt,
          status: 'interviewed', 
          employer_notes: interviewData.notes || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', applicationId)
        .select()
        .single();

      if (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Supabase error in scheduleInterview:', error);
        }
        return { success: false, error: error.message || 'Failed to schedule interview' };
      }

      if (!data) {
        return { success: false, error: 'Application not found or you do not have permission to schedule interview' };
      }

      // Send email notification to the VA about the interview
      try {
        const { data: jobData } = await supabase
          .from('jobs')
          .select('title, employer_id')
          .eq('id', data.job_id)
          .single();

        const { data: vaData } = await supabase
          .from('user_profiles')
          .select('email, full_name')
          .eq('id', data.applicant_id)
          .single();

        if (jobData && vaData) {
          await emailService.sendInterviewScheduledNotification(
            vaData.email,
            vaData.full_name,
            {
              jobTitle: jobData.title,
              interviewTime: interviewData.scheduledAt,
              applicationId: applicationId,
              jobId: data.job_id
            }
          );
        }
      } catch (emailError) {
        console.warn('Failed to send email notification:', emailError);
        // Don't fail the whole operation if email fails
      }

      return { 
        success: true, 
        data: {
          ...data,
          message: `Interview scheduled successfully`
        }
      };
    } catch (error) {
      console.error('Error in scheduleInterview:', error);
      return { success: false, error: error.message || 'An unexpected error occurred while scheduling interview' };
    }
  },
};

export default employerService;
